import React, { useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { buildCustomLensSourceDocument } from '../customView/CustomLensView';
import type { Envelope, EnvelopeColumn, EnvelopeRow, LensView } from './types';

export function bridgeFrameHeight(height: number | undefined): number {
  return Math.min(2400, Math.max(320, height ?? 760));
}

export function buildCardBridgeDocument(view: LensView, channel: string): string {
  return buildCustomLensSourceDocument(
    {
      version: 1,
      html: view.html,
      ...(view.css !== undefined ? { css: view.css } : {}),
      ...(view.js !== undefined ? { javascript: view.js } : {}),
      ...(view.height !== undefined ? { height: view.height } : {}),
    },
    channel,
  );
}

const ROLE_ORDER: Record<EnvelopeColumn['role'], number> = { time: 0, dim: 1, measure: 2 };

function dataColumns(columns: Array<EnvelopeColumn>): Array<EnvelopeColumn> {
  return columns.filter((column) => column.role === 'time' || column.role === 'dim' || column.role === 'measure').sort((left, right) => ROLE_ORDER[left.role] - ROLE_ORDER[right.role]);
}

function rekey(row: EnvelopeRow, columns: Array<EnvelopeColumn>, names: Array<string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  names.forEach((name, index) => {
    const column = columns[index];
    if (column && Object.prototype.hasOwnProperty.call(row, column.name)) out[name] = row[column.name];
  });
  return out;
}

export type BridgePayload = {
  lens: { id: string; title: string; description: string; version: number };
  state: { filters: Record<string, unknown>; period: { from?: string; to?: string; summary: string } };
  cards: Array<{
    cardId: string;
    title: string;
    status: 'ready' | 'empty' | 'error';
    columns: Array<string>;
    rows: Array<Record<string, unknown>>;
    refreshedAt?: string;
    message?: string;
    comparison?: { status: 'ready' | 'empty'; columns: Array<string>; rows: Array<Record<string, unknown>> };
  }>;
};

export function buildCardBridgePayload(input: {
  view: LensView;
  envelopes: Array<Envelope>;
  lens: BridgePayload['lens'];
  filters: Record<string, unknown>;
  period: BridgePayload['state']['period'];
}): BridgePayload {
  const byQuery = new Map(input.envelopes.map((envelope) => [envelope.query, envelope]));
  return {
    lens: input.lens,
    state: { filters: input.filters, period: input.period },
    cards: (input.view.cards ?? []).map((card) => {
      const envelope = card.query ? byQuery.get(card.query) : undefined;
      if (!envelope) return { cardId: card.cardId, title: card.title, status: 'empty' as const, columns: card.columns, rows: [] };
      const columns = dataColumns(envelope.columns);
      const rows = envelope.status === 'error' ? [] : envelope.rows.map((row) => rekey(row, columns, card.columns));
      const compared = Object.values(envelope.compare ?? {})[0];
      return {
        cardId: card.cardId,
        title: card.title,
        status: envelope.status === 'error' ? 'error' as const : rows.length ? 'ready' as const : 'empty' as const,
        columns: card.columns,
        rows,
        ...(envelope.refreshedAt ? { refreshedAt: envelope.refreshedAt } : {}),
        ...(envelope.status === 'error' && envelope.error?.message ? { message: envelope.error.message } : {}),
        ...(compared ? { comparison: { status: compared.length ? 'ready' as const : 'empty' as const, columns: card.columns, rows: compared.map((row) => rekey(row, columns, card.columns)) } } : {}),
      };
    }),
  };
}

export function CardBridgeFrame({ view, envelopes, lens, filters, period }: {
  view: LensView;
  envelopes: Array<Envelope>;
  lens: BridgePayload['lens'];
  filters: Record<string, unknown>;
  period: BridgePayload['state']['period'];
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const channel = useMemo(() => uuidv4(), []);
  const source = useMemo(() => buildCardBridgeDocument(view, channel), [view, channel]);
  const candidatePayload = useMemo(() => buildCardBridgePayload({ view, envelopes, lens, filters, period }), [view, envelopes, lens, filters, period]);
  const payloadKey = JSON.stringify(candidatePayload);
  const stablePayloadRef = useRef({ key: payloadKey, value: candidatePayload });
  if (stablePayloadRef.current.key !== payloadKey) {
    stablePayloadRef.current = { key: payloadKey, value: candidatePayload };
  }
  const payload = stablePayloadRef.current.value;
  const [loaded, setLoaded] = useState(false);
  const send = () => frame.current?.contentWindow?.postMessage({ type: 'lens:data', channel, payload }, '*');
  useEffect(send, [payload, source]);
  useEffect(() => setLoaded(false), [source]);
  return (
    <div className="html-view">
      <iframe
        className="view-frame card-bridge"
        ref={frame}
        sandbox="allow-scripts"
        srcDoc={source}
        title={`${lens.title || 'Custom view'} custom view`}
        data-lens-view-drawn={loaded ? 'true' : undefined}
        onLoad={() => {
          send();
          setLoaded(true);
        }}
        style={{ height: bridgeFrameHeight(view.height), width: '100%', border: '1px solid #e8e4da', borderRadius: 4, display: 'block', backgroundColor: '#f6f1e8' }}
      />
    </div>
  );
}
